import { parse } from 'querystring'
import pathRegexp from 'path-to-regexp'
import moment from 'moment'
import type { Route } from '@/models/connect'
import { message, notification } from 'antd'
import { showConfirm } from '@/utils/global'
import { apiCreateShortLink, shortUrlEncrypt } from '@/services/system/config'
import { AppEnvMap } from '@/constants/global'
import type { OptionType, TreeSelectDataType } from '@/typings/index'
import type * as common from '@/typings/common'
import type { IAccountOptionType } from '@/typings/index'
import { marked } from 'marked'

const { REACT_APP_ENV = 'dev' } = process.env
const reg = /(((^https?:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+(?::\d+)?|(?:www.|[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)$/

export const isUrl = (path: string): boolean => reg.test(path)

export const isAntDesignPro = (): boolean => {
  if (ANT_DESIGN_PRO_ONLY_DO_NOT_USE_IN_YOUR_PRODUCTION === 'site') {
    return true
  }
  return window.location.hostname === 'preview.pro.ant.design'
}

// 给官方演示站点用，用于关闭真实开发环境不需要使用的特性
export const isAntDesignProOrDev = (): boolean => {
  const { NODE_ENV } = process.env
  if (NODE_ENV === 'development') {
    return true
  }
  return isAntDesignPro()
}

/**
 * 获取URL参数
 * @param {String} key 参数名
 * @param {Boolean} isDecode 是否解码
 */
export const getURLParams = (key: string, isDecode?: boolean) => {
  const reg = new RegExp(`(^|&)${key}=([^&]*)(&|$)`, 'i')
  const r = decodeURIComponent(window.location.search).split('?')[1]?.match(reg)
  if (r != null) {
    if (isDecode) {
      return decodeURI(r[2])
    }
    return r[2]
  }
  return null
}

export const getPageQuery = () => parse(window.location.href.split('?')[1])

/**
 * props.route.routes
 * @param router [{}]
 * @param pathname string
 */
export const getAuthorityFromRouter = <T extends Route>(
  router: T[] = [],
  pathname: string
): T | undefined => {
  const currentRoute = router.find(
    ({ routes, path = '/', target = '_self', authority = '' }) =>
      authority === pathname ||
      (path && target !== '_blank' && pathRegexp(path).exec(pathname)) ||
      (routes && getAuthorityFromRouter(routes, pathname))
  )
  if (currentRoute && currentRoute.subRoute) {
    const subRoute = currentRoute.subRoute.find(
      route => pathname === (route.authority ? route.authority : route.path)
    )
    return subRoute ?? undefined
  }
  return currentRoute ?? undefined
}

export const getRouteAuthority = (path: string, routeData: Route[]) => {
  let authorities: string[] | string | undefined
  routeData.forEach(route => {
    // match prefix
    if (pathRegexp(`${route.path}/(.*)`).test(`${path}/`)) {
      if (route.authority) {
        authorities = route.authority
      }
      // exact match
      if (route.path === path) {
        authorities = route.authority || authorities
      }
      // get children authority recursively
      if (route.routes) {
        authorities = getRouteAuthority(path, route.routes) || authorities
      }
    }
  })
  return authorities
}

/**
 * 函数防抖，在第一次触发事件时，不立即执行函数，而是给出一个期限值。效果：如果短时间内大量触发同一事件，只会执行一次函数。
 * @param {fn} fn 执行函数
 * @param {Number} delay 执行期限值
 */
export const debounce = function (fn: any, delay: number = 500) {
  let timer: any = null
  let arg: any
  return function (...args: any) {
    arg = arguments
    timer && clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(null, arg)
    }, delay)
  }
}

/**
 * 函数节流
 * @param fn
 * @param wait
 */
export const throttle = function (fn: any, wait: number = 500) {
  let timer: any = null
  let arg: any
  return function () {
    arg = arguments
    if (!timer) {
      timer = setTimeout(() => {
        fn.apply(null, arg)
        timer = null
      }, wait)
    }
  }
}

/**
 * 下载流，不兼容ie9
 * @param {String} fileName 文件名(不处理)
 * @param {blob} sourceBlob 字节流
 */

export const downloadFile = function (fileName: string, sourceBlob: Blob) {
  const blob = new Blob([sourceBlob])
  if ('download' in document.createElement('a')) {
    // 非IE下载
    const elink = document.createElement('a')
    elink.download = fileName
    elink.style.display = 'none'
    elink.href = URL.createObjectURL(blob)
    document.body.appendChild(elink)
    elink.click()
    URL.revokeObjectURL(elink.href) // 释放URL 对象
    document.body.removeChild(elink)
  } else {
    // IE10+下载
    navigator.msSaveBlob(blob, fileName)
  }
}
export const downloadByrl = async (url: string, fileName?: string) => {
  try {
    // 1. 【关键】轻量检查链接是否有效（只请求请求头，不下载文件内容）
    const response = await fetch(url, {
      method: 'OPTIONS' // 最轻量请求，仅校验链接状态，推荐！
    })
    // 2. 校验链接是否正常
    if (!response.ok) {
      throw new Error(`下载链接异常，状态码：${response.status}`)
    }
    // 1. 创建a标签
    const a = document.createElement('a')
    // 2. 赋值后端给的下载地址
    a.href = url
    // 3. 自定义文件名（可选，不传就用后端自动返回的文件名）
    if (fileName) {
      a.download = fileName
    }
    // 4. 隐藏标签
    a.style.display = 'none'
    document.body.appendChild(a)
    // 5. 触发点击下载
    a.click()
    // 6. 销毁标签（清理DOM）
    document.body.removeChild(a)
  } catch (error) {
    // 4. 捕获所有错误：链接失效、404、403、无权限、跨域等
    console.error('❌ 下载失败：', error)
    const errMsg = (error as Error).message

    // 👇 这里可以替换成你项目的 UI 提示（如 Element UI / AntD 弹窗）
    message.error(`下载失败：${errMsg}`)
  }
}
/**
 * 通用上传文件接口
 * @param formData 接口参数
 * @param progressCallback 进度条监听回调函数
 */
export const fileUpload = (formData: any, progressCallback: any) => {
  const fileSize = formData.get('file').size / 1024 || 1

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/v2/file/upload')
    xhr.onload = evt => {
      const response = JSON.parse(evt?.target?.response)
      if (response.file_id) {
        resolve(response.file_id)
      } else {
        notification.error({
          message: response.msg || '系统开小差了!'
        })
        const formDataFile = formData.get('file')
        reject(formDataFile)
      }
    }
    xhr.onerror = error => {
      // console.log('请求失败',error);
    }
    xhr.upload.onprogress = evt => {
      const percent = parseInt((evt.loaded / evt.total) * 100)
      progressCallback(Math.floor(percent / 2), fileSize)
    }
    xhr.send(formData)
  })
}

/**
 * 通用导入文件接口
 * @param formData 接口参数
 * @param progressCallback 进度条监听回调函数
 */
export const fileImport = (formData: any, importRequestPath: string) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', importRequestPath)
    xhr.onload = evt => {
      const response = JSON.parse(evt?.target?.response)
      if (!response.code || response.code === 0) {
        resolve(response.data)
      } else {
        notification.error({
          message: response.message || '系统开小差了!'
        })
        const formDataFile = formData.get('file')
        reject(formDataFile)
      }
    }
    xhr.onerror = error => {
      reject(error)
      // console.log('请求失败',error);
    }
    xhr.send(formData)
  })
}

/**
 * 字符转换成1-10以内整数
 * @param str 字符
 */
export const transformNumber = (str?: string) => {
  return Number(str?.charCodeAt(0)?.toString(10)) % 10
}

/**
 * 本地存储,默认有效期30天
 */
export const storage = {
  set: (key: string, value: any, expirse?: number) => {
    expirse = expirse || 30 * 24 * 60 * 60 * 1000
    const data = { value, ctime: new Date().getTime(), expirse }
    try {
      localStorage.setItem(key, JSON.stringify(data))
    } catch (error) {
      console.error(error)
    }
  },
  setValue: (key: string, name: string, value: any) => {
    try {
      const data = JSON.parse(localStorage.getItem(key) as string)
      if (data && data.value) {
        data.value[name] = value
        storage.set(key, data.value)
      }
    } catch (error) {
      return false
    }
  },
  get: (key: string) => {
    storage.clearExpirse()
    try {
      const data = JSON.parse(localStorage.getItem(key) as string)
      if (data !== null) {
        if (data.expirse === 0 || new Date().getTime() - data.ctime > data.expirse) {
          localStorage.removeItem(key)
        } else {
          return data.value
        }
      }
      return null
    } catch (error) {
      return false
    }
  },
  remove: (key: string) => {
    localStorage.removeItem(key)
  },
  clearExpirse: () => {
    const keys: string[] = Object.keys(localStorage)
    keys.forEach(key => {
      try {
        const data = JSON.parse(localStorage.getItem(key) as string)
        if (data !== null) {
          if (data.expirse === 0 || new Date().getTime() - data.ctime > data.expirse) {
            localStorage.removeItem(key)
          }
        }
      } catch (error) {
        return false
      }
    })
  }
}

interface DragParms {
  element: any
  horizontal?: boolean
  vertical?: boolean
}
// 拖拽
export const drag = (params: DragParms) => {
  const { element = null, horizontal = true, vertical = true } = params
  const ele = element
  if (!ele) return
  let isDown = false // 鼠标是否按下
  let left = 0 // 当前元素x坐标
  let top = 0 // 当前元素y坐标
  const win_w = document.body.clientWidth // 窗口宽度
  const win_h = document.body.clientHeight // 窗口高度
  let ele_w = ele.offsetWidth // 元素宽度
  let ele_h = ele.offsetHeight // 元素高度
  let innerX = 0 // 元素内x坐标
  let innerY = 0 // 元素内Y坐标

  // ele.style.left = win_w - ele_w - 60 + 'px';
  // ele.style.top = win_h - ele_h - 30 + 'px';
  ele.onmousedown = e => {
    ele_w = ele.offsetWidth
    ele_h = ele.offsetHeight
    top = ele.offsetTop
    left = ele.offsetLeft
    innerX = e.clientX - left
    innerY = e.clientY - top
    isDown = true
  }
  window.onmousemove = e => {
    if (!isDown) return
    if (horizontal) {
      ele.style.left = `${e.clientX - innerX}px`
    }
    if (vertical) {
      ele.style.top = `${e.clientY - innerY}px`
    }
    if (parseInt(ele.style.top) <= 0) {
      ele.style.top = '0'
    }
    if (parseInt(ele.style.left) <= 0) {
      ele.style.left = '0'
    }
    if (parseInt(ele.style.left) + ele_w >= win_w) {
      ele.style.left = `${win_w - ele_w}px`
    }
    if (parseInt(ele.style.top) + ele_h >= win_h) {
      ele.style.top = `${win_h - ele_h}px`
    }
  }
  window.onmouseup = () => {
    isDown = false
  }
}

// braft-editor 媒体上传
export const braftEditorUploadFn = (param: any) => {
  const serverURL = '/api/v2/file/upload_image'
  const xhr = new XMLHttpRequest()
  const fd = new FormData()

  const successFn = () => {
    // 假设服务端直接返回文件上传后的地址
    // 上传成功后调用param.success并传入上传后的文件地址
    try {
      const res = JSON.parse(xhr.responseText)
      if (res.url) {
        param.success({
          url: res.url,
          meta: {
            // id: 'xxx',
            title: (param && param.file && param.file.name) || '',
            alt: (param && param.file && param.file.name) || '',
            loop: false, // 指定音视频是否循环播放
            autoPlay: false, // 指定音视频是否自动播放
            controls: true // 指定音视频是否显示控制栏
            // poster: 'http://xxx/xx.png', // 指定视频播放器的封面
          }
        })
      } else {
        notification.error({
          message: '提示',
          description: (res && res.message) || '上传失败'
        })
      }
    } catch (error) {
      param.error({
        msg: '上传失败'
      })
    }
  }

  const progressFn = (event: any) => {
    // 上传进度发生变化时调用param.progress
    param.progress((event.loaded / event.total) * 100)
  }

  const errorFn = () => {
    // 上传发生错误时调用param.error
    param.error({
      msg: '上传失败'
    })
  }

  xhr.upload.addEventListener('progress', progressFn, false)
  xhr.addEventListener('load', successFn, false)
  xhr.addEventListener('error', errorFn, false)
  xhr.addEventListener('abort', errorFn, false)

  fd.append('file', param.file)
  fd.append('upload_path', '/project/images')
  fd.append('business_type', '5')
  xhr.open('POST', serverURL, true)
  xhr.send(fd)
}

/**
 * 将有父子关系的菜单列表转换成树形结构数据
 * @param data 菜单列表
 * @param isFilter 是否过滤未开启菜单
 * @returns
 */
export const translateDataToTree = function (data = [], isFilter?: boolean) {
  isFilter = isFilter || false

  const translatorFun = (parents: any[], childrens: any[]) => {
    parents.forEach(p => {
      p.children = []
      p.checkboxGroup = []
      childrens.forEach((c, cIndex) => {
        if (isFilter && c.is_enable === 0) {
        } else if (p.code === c.parent_code && c.type !== 2) {
          const temp = JSON.parse(JSON.stringify(childrens))
          temp.splice(cIndex, 1)
          translatorFun([c], temp)
          p.children.push(c)
        } else if (p.code === c.parent_code && c.type === 2) {
          if (isFilter && c.is_enable === 0) {
            return
          }
          p.checkboxGroup.push(c)
        }
      })
    })
  }

  const arrayDeepSort = (arr: any[]) => {
    const handleSort = (newArr: any[]) => {
      newArr.sort((a, b) => {
        return a.priority - b.priority
      })
      newArr.forEach(li => {
        if (li.children && li.children.length !== 0) {
          handleSort(li.children)
        }
      })
    }
    handleSort(arr)
    return arr
  }

  const newArr = arrayDeepSort(data)
  let parents = newArr.filter((li: any) => {
    return li.parent_code === '0'
  })
  let childrens = newArr.filter((li: any) => {
    return li.parent_code !== '0'
  })
  if (isFilter) {
    parents = parents.filter((li: any) => li.is_enable !== 0)
    childrens = childrens.filter((li: any) => li.is_enable !== 0)
  }

  // 调用转换方法
  translatorFun(parents, childrens)

  // 返回最终的结果
  return parents
}

export const isEmpty = (value: any) => {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
    return true
  }
  return false
}

// 判断数组是否有重复项
export const arrIsRepeat = (arr: any[]) => {
  const hash = {}
  for (const i in arr) {
    if (hash[arr[i]]) {
      return true
    }
    hash[arr[i]] = true
  }
  return false
}

// 版本检测
export const checkingVersion = () => {
  if (process.env.NODE_ENV == 'production') {
    // 在 js 中请求首页地址不会更新页面
    const serverURL = `${window.location.protocol}//${
      window.location.host
    }?t=${new Date().getTime()}`
    const xhr = new XMLHttpRequest()
    xhr.addEventListener(
      'load',
      () => {
        try {
          if (xhr.status != 200) {
            return
          }

          const el = document.createElement('html')
          el.innerHTML = xhr.responseText

          const isShowVersionDialog = window.localStorage.getItem('isShowVersionDialog') || 0

          // 拿到 当前hash
          const new_scriptArr = el.getElementsByTagName('body')[0].getElementsByTagName('script')
          const new_mainSrc = Array.from(new_scriptArr).find(li => li.src.includes('umi.'))
          const new_indexSrc = new_mainSrc.src.split('/')
          const new_hash = new_indexSrc[new_indexSrc.length - 1].split('.')[1]
          const cur_scriptArr = document
            .getElementsByTagName('body')[0]
            .getElementsByTagName('script')
          const cur_mainSrc = Array.from(cur_scriptArr).find(li => li.src.includes('umi.'))
          const cur_indexSrc = cur_mainSrc.src.split('/')
          const cur_hash = cur_indexSrc[cur_indexSrc.length - 1].split('.')[1]
          if (isShowVersionDialog == 0 && cur_hash != new_hash) {
            showConfirm({
              title: '检测到新版本，是否刷新页面进行更新?',
              okText: '刷新',
              cancelText: '取消',
              onOk() {
                window.location.reload(true)
              },
              onCancel() {}
            })
            window.localStorage.setItem('isShowVersionDialog', '1')
          }
        } catch (error) {}
      },
      false
    )
    xhr.open('GET', serverURL, true)
    xhr.send()
  }
}

export const getUrlParam = (variable: string) => {
  const query = window.location.search.substring(1)
  const vars = query.split('&')
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split('=')
    if (pair[0] == variable) {
      return pair[1]
    }
  }
  return ''
}

export const getPublicStr = (s1: string, s2: string) => {
  let sstr = ''
  let L1 = s1.length
  const L2 = s2.length
  if (L1 > L2) {
    const s3 = s1
    s1 = s2
    s2 = s3
    L1 = s2.length
  }
  for (let j = L1; j > 0; j--) {
    for (let i = 0; i <= L1 - j; i++) {
      sstr = s1.substr(i, j)
      if (s2.indexOf(sstr) >= 0) {
        return sstr
      }
    }
  }
  return ''
}

export const FindIndex = (array: any[], com: string) => {
  return array.findIndex(item => item.label === com)
}

export const Classification = (data: any[], circleList: any[]) => {
  const res: any = circleList.map(circle => {
    return {
      label: circle.value || circle.name,
      value: circle.key || circle.code,
      options: []
    }
  })
  data.map((item: any) => {
    const index = FindIndex(res, item.circle)
    const newItem = {
      name: item.name,
      label: item?.label || item.name,
      value: item.id,
      disabled: item.disabled
    }
    if (index >= 0) {
      res[index].options.push(newItem)
    } else {
      const obj: any = {}
      obj.label = item.circle
      obj.value = item.circle_code
      obj.options = [newItem]
      res.push(obj)
    }
  })
  const list = res.filter((circle: any) => circle.options.length !== 0)
  return list
}

export const splitFloat = (val: string, placeNum: number = 2) => {
  return +parseFloat(val).toFixedRound(placeNum)
}
export const splitFloatStr = (val: string, placeNum: number = 2) => {
  return parseFloat(val).toFixedRound(placeNum)
}
export const formatNumber = num => {
  return `${num}`.replace(/\d+/, function (match) {
    return match.replace(/(\d)(?=(\d{3})+$)/g, '$1,')
  })
}

// 长链接字符串加密获取端链接
export const getShortCode = async (longUrl: string) => {
  const { domain, key } = AppEnvMap[REACT_APP_ENV]
  if (domain && longUrl) {
    const encodeUrl = encodeURIComponent(`${key}://${longUrl}`)
    const { code, data } = await shortUrlEncrypt({ long_url: encodeUrl })
    if (code === 0) {
      const url = `https://${domain}.5ycap.com/landing/${data.short_url}`
      const res = await apiCreateShortLink({
        long_url: url
      })
      return res.data?.info?.short_url || ''
    }
    return ''
  }
  return ''
}

// 根据月份和日期计算星座
export const getAstro = (strBirthday: string) => {
  const strBirthdayArr = strBirthday.split('-')
  const birthMonth = +strBirthdayArr[1]
  const birthDay = +strBirthdayArr[2]
  return '魔羯水瓶双鱼白羊金牛双子巨蟹狮子处女天秤天蝎射手魔羯'.substr(
    birthMonth * 2 - (birthDay < '102223444433'.charAt(birthMonth - 1) - -19) * 2,
    2
  )
}

export const getAge = (strBirthday: string) => {
  // 根据出生日期算出年龄 getAge('1995-09-26')
  let returnAge
  const strBirthdayArr = strBirthday.split('-')
  const birthYear = +strBirthdayArr[0]
  const birthMonth = +strBirthdayArr[1]
  const birthDay = +strBirthdayArr[2]
  const d = new Date()
  const nowYear = d.getFullYear()
  const nowMonth = d.getMonth() + 1
  const nowDay = d.getDate()
  if (nowYear == birthYear) {
    returnAge = 0 // 同年 则为0岁
  } else {
    const ageDiff = nowYear - birthYear // 年之差
    if (ageDiff > 0) {
      if (nowMonth == birthMonth) {
        const dayDiff = nowDay - birthDay // 日之差
        if (dayDiff < 0) {
          returnAge = ageDiff - 1
        } else {
          returnAge = ageDiff
        }
      } else {
        const monthDiff = nowMonth - birthMonth // 月之差
        if (monthDiff < 0) {
          returnAge = ageDiff - 1
        } else {
          returnAge = ageDiff
        }
      }
    } else {
      returnAge = -1 // 返回-1 表示出生日期输入错误 晚于今天
    }
  }
  return returnAge // 返回周岁年龄
}

export const turnChineseNumber = (index: number) => {
  const ChineseNumberArr = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十']
  return ChineseNumberArr[index]
}

export const toMoment = (time: number | null | undefined) => {
  return time ? moment(time * 1000) : null
}

export const filterOption = (optionList: OptionType[], initOption: OptionType | null) => {
  if (!initOption) {
    return optionList
  }
  if (optionList.map(item => item.value).includes(initOption.value)) {
    return optionList
  }
  return [...optionList, { ...initOption, disabled: true }]
}

export const filterAccountOption = (
  optionList: IAccountOptionType[] = [],
  initUser?: common.Iuser
) => {
  if (!initUser?.id) {
    return optionList
  }

  if (optionList.map(item => item.value).includes(initUser.id)) {
    return optionList
  }
  const initOption = {
    label: initUser.name_en,
    value: initUser.id
  }
  return [...optionList, { ...initOption, disabled: true }]
}
export const checkNullObj = (obj: Record<string, any>) => {
  return Object.keys(obj).length === 0
}

export const isTrue = (val: number) => {
  // 1: true, 2或0:false
  return val === 1
}

type FormatTreeFun = (...args: any) => TreeSelectDataType[]
export const formatTree: FormatTreeFun = (
  data: Record<string, any>,
  valueKey: string,
  titleKey: string
) => {
  const formatData: FormatTreeFun = list => {
    return list.map((item: any) => {
      const node: TreeSelectDataType = {
        value: item[valueKey],
        title: item[titleKey],
        children: formatData(item.children)
      }
      return node
    })
  }
  return formatData(data)
}
// 判断是否为aomao的富文本内容
export const checkEditorType = (val: string) => {
  // 无内容默认为aomao的富文本
  if (!val) {
    return 'aomao'
  }
  const dom = new DOMParser().parseFromString(val, 'text/html')
  const hasAoMaoAttribute = dom.body.firstElementChild?.getAttribute('data-id')
  const hasAoMaoTag = dom.getElementsByTagName('card')
  if (hasAoMaoAttribute || (hasAoMaoTag && hasAoMaoTag.length > 0)) {
    return 'aomao'
  }
  return 'tinymac'
}

// 对字符串集合随机排列，随机输出指定的长度
export const randomString = (length = 10) => {
  const str = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let result = ''
  for (let i = length; i > 0; --i) result += str[Math.floor(Math.random() * str.length)]
  return result.toLowerCase()
}

export const formatUnixTime = (timestamp?: number, format = 'YYYY-MM-DD') => {
  if (!timestamp || timestamp < 0) {
    return ''
  }
  return moment.unix(+timestamp).format(format)
}
export const unixToTime = (timestamp?: number | string) => {
  if (!timestamp || +timestamp < 0) {
    return undefined
  }
  if (!+timestamp) {
    return undefined
  }
  return moment.unix(+timestamp)
}
export const dateToUnix = (date?: moment.MomentInput) => {
  if (!date) {
    return undefined
  }
  return moment(date).unix()
}
export const dateToStartUnix = (
  date?: moment.MomentInput,
  unitOfTime: moment.unitOfTime.StartOf = 'day'
) => {
  if (!date) {
    return undefined
  }
  return moment(date).startOf(unitOfTime).unix()
}
export const dateToEndUnix = (
  date?: moment.MomentInput,
  unitOfTime: moment.unitOfTime.StartOf = 'day'
) => {
  if (!date) {
    return undefined
  }
  return moment(date).endOf(unitOfTime).unix()
}

/** 将k-v形式的type-text转成select，option */
export const typeToSelectOptions = <T extends Record<string | number, string>>(
  typeText: T
): { value: keyof T; label: string }[] => {
  return Object.entries(typeText).map(([key, text]) => {
    return { value: +key || key, label: text }
  })
}
export const memberListToValueList = (memberList: common.Iuser[] = []) => {
  return memberList.map(item => ({
    text: item.name_en,
    val: item.id
  }))
}
/** 返回新对象，对象的0和空字符串会转成undefined,主要用来转化类型字段，防止在select上展示0 */
export function transEmptyValue<T extends Record<string, any>>(obj: T): T {
  const newForm = {} as T
  Object.keys(obj || {}).forEach(key => {
    const k = key as keyof T
    newForm[k] = (obj?.[k] || undefined) as any
  })
  return newForm
}
/** 强制刷新 */
export function forceRefresh() {
  // 设置只强制刷行一次页面
  if (location.href.indexOf('#reloaded') === -1) {
    location.href = location.href + '#reloaded'
    window.location.reload()
  }
}
interface NestedItem<T> {
  children?: T[]
  [property: string]: any
}

export function traverseNestedArray<T extends NestedItem<T>>(
  items: T[],
  callback: (item: T, level: number) => void,
  level: number = 0
): void {
  // 遍历当前层级的每个元素
  items.forEach(item => {
    // 调用回调函数处理当前元素
    callback(item, level)

    // 如果存在子元素数组，则递归遍历
    if (item.children && item.children.length > 0) {
      traverseNestedArray(item.children, callback, level + 1)
    }
  })
}
/**
 * 辅助方法：基础 XSS 防护（转义危险 HTML 标签和属性）
 * @param str 需要处理的字符串
 * @returns 处理后的安全字符串
 */
function escapeHtmlForXss(str: string): string {
  if (!str) return ''
  return (
    str
      // 转义 HTML 标签的尖括号
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // 转义引号（防止属性注入）
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
      // 过滤危险的 JavaScript 协议（如 javascript:xxx）
      .replace(/javascript:/gi, 'javascript&#58;')
      .replace(/vbscript:/gi, 'vbscript&#58;')
  )
}
/**
 * Markdown 转 HTML
 * @param markdownStr Markdown 字符串
 * @param options 配置选项
 */
export function markdownToHtml(
  markdownStr: string | null | undefined,
  options: {
    markedOptions?: any
    enableXssProtection?: boolean
  } = {}
): string {
  if (!markdownStr) {
    return ''
  }

  const defaultMarkedOptions = {
    gfm: true,
    breaks: true,
    pedantic: false,
    smartLists: true,
    headerIds: false,
    mangle: false
  }

  // 自定义 renderer，让链接在新窗口打开
  const userRenderer = options.markedOptions?.renderer
  const renderer = userRenderer || new marked.Renderer()
  const originalLink = renderer.link.bind(renderer)
  renderer.link = (href: string, title: string, text: string) => {
    const link = originalLink(href, title, text)
    // 如果链接已经包含 target 属性，则不修改；否则添加 target="_blank"
    if (link.includes('target=')) {
      return link
    }
    return link.replace('<a ', '<a target="_blank" ')
  }

  const finalMarkedOptions = {
    ...defaultMarkedOptions,
    ...options.markedOptions,
    renderer
  }

  marked.setOptions(finalMarkedOptions)

  try {
    const parsed = marked.parse(markdownStr)
    let html = typeof parsed === 'string' ? parsed : String(parsed)

    if (options.enableXssProtection !== false) {
      html = escapeHtmlForXss(html)
    }

    return html
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Markdown 转 HTML 失败：', error)
    return `<div style="color: red; font-weight: bold;">Markdown 解析错误：${
      (error as Error).message
    }</div>`
  }
}
export const sleep = async (time: number) => {
  return new Promise<void>((resolve, reject) => {
    setTimeout(() => {
      resolve()
    }, time)
  })
}
